import * as _ from 'lodash';
import * as React from 'react';
import { BtnPopover } from '../BtnPopover';
import { BtnSelect, IBtnSelectOptGroup } from '../BtnSelect';
import {CustomRangeSelector} from '../CustomRangeSelector';
import { IMultiNode } from '../DataMultiBtnSelect';

interface IMultiBtnSelectProps {
  placeholder: string
  persistLabel?: boolean
  hideSelectedValue?: boolean
  source?: IMultiNode[]
  values?: string[]
  onChange?: (values: string[]) => void
  onClosed?: () => void
  btnRef?: (ref: HTMLElement) => void
  disabled?: boolean
  useMobileMode?: boolean
  showSpinner?: boolean
  defaultValue?: string
  disableClearOnFirstSelect?: boolean
  hidePopoverOnClear?: boolean
  hidePopoverOnLeadNodeSelect?: boolean
  hideFirstSelect?: boolean
  displayAllOption?: boolean
  showAllCoachingFormOption?: boolean
  useLinkView?: boolean
  rightAlign?: boolean

  updateDateRange: (startDate: Date, endDate: Date) => void

  startDateRange: Date
  endDateRange: Date
  disableDatePicker: boolean
  disableSubmitButton?: boolean;
  isDataLoading?: boolean;
}

interface IMultiBtnSelectState {
  btnToggleFn?: (show: boolean) => void
  autoHide: boolean
}

export class CustomPeriodMultiBtnSelect extends React.Component<IMultiBtnSelectProps, IMultiBtnSelectState> {
  state: IMultiBtnSelectState = {
    autoHide: true,
  }

  hasLoaded = false

  componentWillMount() {
    this.checkForDefault()
  }

  componentWillReceiveProps(nextProps: IMultiBtnSelectProps) {
    this.checkForReset(nextProps)
    this.checkForDefault(nextProps)
    this.checkForValidity(nextProps)
  }

  checkForValidity(props = this.props) {
    const { values = [], source = [], onChange = () => ({}), defaultValue } = props

    if (values.length < 1 || source.length < 1 || !defaultValue) { return }

    const rootSource = source.find((s) => s.value === values[0])

    if (!rootSource) { return false }

    let currentSource = rootSource
    const nodeValues = values.filter((v, index) => index !== 0);

    const isValid = nodeValues.every((value) => {
      if (!currentSource.nodes) { return false }

      const index = currentSource.nodes.findIndex((cs) => cs.value === value)

      currentSource = currentSource.nodes[index]

      return index >= 0
    })

    if (!isValid) {
      const validDefaultValue = values[values.length - 1] || defaultValue
      const validValues = this.getDefaultValuesFor(validDefaultValue, [], source)

      if (validValues.length > 0) {
        onChange(validValues)
      }
    }
  }

  /**
   * Sometimes the user resets all the fiters and the
   * server returns a single value to reset to. This check makes
   * sure that the values array is correct, if not, it rebuilds
   * the values array correctly.
   * @param props
   */
  checkForReset(props: IMultiBtnSelectProps) {
    const { values = [], source = [], onChange = () => ({}) } = props

    if (values.length !== 1 || source.length < 1) { return }

    const value = values[0]
    const defaultValues = this.getDefaultValuesFor(value, [], source)

    if (defaultValues.length === 0) { return }

    const needsReset = !_.isEqual(values, defaultValues)

    if (needsReset) {
      onChange(defaultValues)
    }
  }

  checkForDefault(props = this.props) {
    const { values = [], defaultValue = '', source = [], onChange = () => ({}) } = props

    if (values.length === 0 && !!defaultValue && !this.hasLoaded && source.length > 0) {
      const defaultValues = this.getDefaultValuesFor(defaultValue, [], source)

      onChange(defaultValues)
      this.hasLoaded = true
    }
  }

  updateValues(nodes: IMultiNode[], newValue: string, depth: number) {
    let { values = [] } = this.props
    const { source, hidePopoverOnLeadNodeSelect } = this.props
    const { btnToggleFn } = this.state
    let selectedNode = nodes.find((n) => n.value === newValue)
    const deleteCurrentDepth = newValue === ''
    const depthToDeleteAt = deleteCurrentDepth ? depth : depth + 1

    values[depth] = newValue

    //Delete later values
    values = values.slice(0, depthToDeleteAt)

    //NOTE: If we are on the coaching forms overview and the selected hier is ALL then selectable = true
    if (window.location.href.indexOf('coachingoverview') !== -1 && selectedNode && selectedNode.label === 'All Coaching Forms') {
      selectedNode.selectable = true
    }

    if (source && hidePopoverOnLeadNodeSelect && btnToggleFn) {
      const isLeafNode = this.isLeafNode(source, values)

      if (isLeafNode) {
        btnToggleFn(false)
      }
    }

    while (true) {
      if (!selectedNode) { break; }

      const selectedNodeIsNotSelectable = !selectedNode.selectable || selectedNode.groupOption
      const { nodes: selectedNodes = [] } = selectedNode

      //If not selectable, default to first node in next nodes
      if (selectedNodeIsNotSelectable && selectedNodes.length > 0) {
        const firstNode = selectedNodes[0]
        const nextValue = firstNode.value;

        values.push(nextValue)

        selectedNode = selectedNodes.find((n) => n.value === nextValue)
      } else {
        break;
      }
    }

    if (this.props.onChange) {
      this.props.onChange(values)
    }
  }

  isLeafNode(nodes: IMultiNode[], values: string[]): boolean {
    values = [...values]
    const value = values.splice(0, 1).pop()
    const node = nodes.find((n) => n.value === value)

    if (!value) {
      return nodes.length === 0
    } else if (!node) {
      return values.length === 0
    } else if (!node.nodes) {
      return false
    }

    return this.isLeafNode(node.nodes, values)
  }

  getDefaultValuesFor(defaultValue: string, currentValues: string[], currentNodes: IMultiNode[]): string[] {
    const defaultNode = currentNodes.find((cn) => cn.value === defaultValue)
    let foundValues: string[] | undefined;

    if (defaultNode) {
      currentValues.push(defaultNode.value)

      if (!defaultNode.selectable && defaultNode.nodes) {
        const selectedNode = defaultNode.nodes[0]

        if (selectedNode) {
          currentValues.push(selectedNode.value)
        }
      }

      return currentValues
    }

    currentNodes.every((nextNodes) => {
      const hasNodes = nextNodes.nodes && nextNodes.nodes.length > 0

      if (hasNodes) {
        const nextValues = [...currentValues, nextNodes.value]
        const nestedNodes = nextNodes.nodes
        if (nestedNodes !== undefined) {
          const possibleValues = this.getDefaultValuesFor(defaultValue, nextValues, nestedNodes)
          const hasPossibleValues = possibleValues !== undefined && possibleValues.length > 0

          if (hasPossibleValues) {
            foundValues = possibleValues
            return false //break search
          }
        }
      }

      return true //continue search
    })

    return foundValues ? foundValues : []
  }

  onClear() {
    const { btnToggleFn } = this.state;
    const { hidePopoverOnClear = false } = this.props;

    if (btnToggleFn && hidePopoverOnClear) {
      btnToggleFn(false)
    }
  }

  setAutoHide(autoHide: boolean) {
    this.setState({autoHide})
  }

  render() {
    const { placeholder, persistLabel, hideSelectedValue, defaultValue, hideFirstSelect, displayAllOption = false, updateDateRange, startDateRange, endDateRange, disableDatePicker, isDataLoading, disableSubmitButton } = this.props
    const { useMobileMode = false, showSpinner = false, source = [], disableClearOnFirstSelect = false, showAllCoachingFormOption = false } = this.props
    let { values = [] } = this.props
    const { autoHide } = this.state;
    let header: string | JSX.Element = placeholder
    let currentSource: IMultiNode[] | undefined = source
    let selectedLabel = ''
    const currentSourceHasParent = Array.isArray(currentSource) && currentSource[0]
    let isClearable = currentSourceHasParent ? currentSource[0].selectable : true

    //For next selected
    values = [...values, '']

    const selects = values.map((value, depth) => {
      if (currentSource !== undefined && currentSource.length > 0) {
        const currentSourceCopy = [...currentSource]
        const nextSource = currentSource.find((s) => s.value === value)
        const depthIsZero = depth === 0
        const displayAllCoachingFormOption = (showAllCoachingFormOption && depth === 1)
        const clearable = disableClearOnFirstSelect && depthIsZero ? false : isClearable
        let groupOptions: IBtnSelectOptGroup[];
        let isGroupOption = nextSource && nextSource.groupOption

        if (isGroupOption) {
          groupOptions = currentSource.map(({ label, nodes }) => ({ label, nodes }))
          value = values[depth + 1]
        }

        let select = null
        if (value !== 'custom_range_selector') {
          select = (
            <BtnSelect key={value + depth}
                       clearable={clearable}
                       clearLabel={`All ${selectedLabel}`}
                       header={`All ${selectedLabel}`}
                       value={value}
                       disabled={false}
                       groupOptions={groupOptions}
                       options={!isGroupOption && currentSource}
                       showAllCoachingFormOption={displayAllCoachingFormOption}
                       onChange={(updatedValue, updatedLabel) => this.updateValues(currentSourceCopy, updatedValue, depth)}
                       onClear={() => this.onClear()}
            />
          )
        } else {
          select = (
            <div style={{margin: '20px', marginTop: 0, minWidth: '120px'}} key={'custom_range_selector'}>
              <CustomRangeSelector
                isDataLoading={isDataLoading}
                updateDateRange={(startDate, endDate) => {
                  this.setAutoHide(true);
                  updateDateRange(startDate, endDate)
                }}
                disableAutoHide={() => {this.setAutoHide(false)}}
                startDateRange={startDateRange}
                endDateRange={endDateRange}
                disableDatePicker={disableDatePicker}
                disableSubmitButton={disableSubmitButton}
              />
            </div>
          )
        }

        if (isGroupOption) {
          //kludge -- only works when group option is last option
          //--signed Paden
          currentSource = undefined
          if (nextSource && nextSource.nodes) {
            const newNode = nextSource.nodes.find((n) => n.value === value)
            selectedLabel = newNode && newNode.label ? newNode.label : value
          }
        } else if (nextSource) {
          isClearable = nextSource ? nextSource.selectable : false
          selectedLabel = nextSource.label || nextSource.value
          isGroupOption = nextSource.groupOption ? nextSource.groupOption : false
          currentSource = nextSource.nodes ? nextSource.nodes : undefined
        }

        if (hideFirstSelect && depthIsZero) {
          return null
        }

        return select
      }

      return null
    }).filter((select) => select !== null && select !== undefined)

    if (!useMobileMode && selectedLabel && !hideSelectedValue) {
      if (persistLabel) {
        header = (
          <>
            <b className="fw-600">{header}:</b>
            <span>&nbsp;{selectedLabel}</span>
          </>
        )
      } else {
        header = selectedLabel
      }
    }

    return (
      <BtnPopover
        disableAutoHiding={defaultValue === 'custom_range_selector' || !autoHide}
        header={header}
        rightAlign={this.props.rightAlign}
        useLinkView={this.props.useLinkView}
        btnRef={this.props.btnRef}
        showSpinner={showSpinner}
        useMobileMode={useMobileMode}
        onClosed={this.props.onClosed}
        disabled={this.props.disabled}
        getToggleFn={(btnToggleFn) => this.setState({ btnToggleFn })}>
        {selects !== undefined && selects.length > 0 ?
          <section className={`d-flex ${useMobileMode ? 'justify-content-start' : 'justify-content-between'}`}>
            {selects}
          </section>
          : null
        }
      </BtnPopover>
    )
  }
}
